import React, { FC, useContext, useEffect, useState } from 'react';

import { useQuery } from '@apollo/client';
import {
  Button,
  ContextMenu,
  Input,
  NumberInput,
  Pagination,
  Preloader,
  SearchIcon,
  Table,
  Tabs,
  useContextMenu,
} from '@smartshell/ui-kit';
import { useNavigate } from 'react-router';
import { useLocation } from 'react-router-dom';

import { GeneratePromoModal } from 'components';
import { LayoutContext } from 'components/Layout';
import { LocalizationContext } from 'localization';
import { ClientModal, DepositModal } from 'modals';
import {
  GET_PROMO_CAMPAIGNS,
  GET_PROMO_CODES,
  IPromoCampaignResponse,
  IPromoCodesResponse,
  TPromoCampaignData,
  TPromoCodeData,
} from 'schemas';
import { SettingsContext } from 'settings';
import { EPromoCodeMode } from 'types';

import {
  useColumnsCampaign,
  useColumnsPromo,
  useContextMenuItemsCampaign,
  useContextMenuItemsPromo,
} from './hooks';
import s from './PromoCodes.module.scss';

export const PromoCodes: FC = () => {
  const { t } = useContext(LocalizationContext);
  const {
    state: {
      settings: { currency },
    },
  } = useContext(SettingsContext);
  const layoutElement = useContext(LayoutContext);
  const navigate = useNavigate();
  const [clientUUID, setClientUUID] = useState<string | null>(null);
  const [isDepositOpen, setIsDepositOpen] = useState<boolean>(false);
  const [searchStr, setSearchStr] = useState('');
  const [openGenerate, setOpenGenerate] = useState<boolean>(false);
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(
    null
  );
  const location = useLocation();
  const [tab, setTab] = useState<EPromoCodeMode>(
    location.state?.activeTab || EPromoCodeMode.SINGLE
  );
  const [currentPagePromo, setCurrentPagePromo] = useState(1);
  const [currentPageCampaign, setCurrentPageCampaign] = useState(1);
  const [promoPaginationInput, setPromoPaginationInput] =
    useState(currentPagePromo);
  const [campaignPaginationInput, setCampaignPaginationInput] =
    useState(currentPageCampaign);

  const {
    data: promoCodes,
    loading: promoCodesLoading,
    refetch: refetchPromo,
  } = useQuery<IPromoCodesResponse>(GET_PROMO_CODES, {
    fetchPolicy: 'cache-and-network',
    variables: {
      filter: undefined,
      page: currentPagePromo,
    },
  });

  const {
    data: promoCampaign,
    loading: promoCampaignLoading,
    refetch: refetchCampaign,
  } = useQuery<IPromoCampaignResponse>(GET_PROMO_CAMPAIGNS, {
    fetchPolicy: 'cache-and-network',
    variables: {
      filter: undefined,
      page: currentPageCampaign,
    },
  });
  function useDebounce<T>(value: T, delay = 400): T {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);

      return () => {
        clearTimeout(handler);
      };
    }, [value, delay]);

    return debouncedValue;
  }

  const debouncedSearch = useDebounce(searchStr, 500);

  useEffect(() => {
    if (debouncedSearch.length > 0 && debouncedSearch.length < 2) {
      return;
    }

    const filter = debouncedSearch ? { q: debouncedSearch } : undefined;

    if (tab === EPromoCodeMode.SINGLE) {
      setCurrentPagePromo(1);
      setPromoPaginationInput(1);

      refetchPromo({
        filter,
        page: 1,
      });
    }

    if (tab === EPromoCodeMode.CAMPAIGN) {
      setCurrentPageCampaign(1);
      setCampaignPaginationInput(1);

      refetchCampaign({
        filter,
        page: 1,
      });
    }
  }, [debouncedSearch, tab, refetchPromo, refetchCampaign]);

  const OPTIONS = [
    {
      title: t('logs.categories.promo'),
      value: EPromoCodeMode.SINGLE,
      count: promoCodes?.promoCodesList.paginatorInfo.total || 0,
    },
    {
      title: t('promo.tab.group'),
      value: EPromoCodeMode.CAMPAIGN,
      count: promoCampaign?.promoCampaignList?.items?.length,
    },
  ];

  const {
    contextMenuData: contextMenuDataPromo,
    closeContextMenu: closeContextMenuPromo,
    cursorPosition: cursorPositionPromo,
    onContextMenu: onContextMenuPromo,
    // onTouchMove,
    // onTouchEnd,
    // onTouchStart,
  } = useContextMenu<TPromoCodeData>();

  const {
    contextMenuData: contextMenuDataCampaign,
    closeContextMenu: closeContextMenuCampaign,
    cursorPosition: cursorPositionCampaign,
    onContextMenu: onContextMenuCampaign,
    // onTouchMove,
    // onTouchEnd,
    // onTouchStart,
  } = useContextMenu<TPromoCampaignData>();

  const handleGenerateClick = (id: string): void => {
    setSelectedCampaignId(id);
    setOpenGenerate(true);
  };

  const handleCloseGenerate = (): void => {
    setOpenGenerate(false);
    setSelectedCampaignId(null);
  };
  const contextMenuItemsPromo = useContextMenuItemsPromo(refetchPromo);
  const contextMenuItemsCampaign = useContextMenuItemsCampaign(
    refetchCampaign,
    handleGenerateClick
  );
  const COLUMNS_PROMO = useColumnsPromo(t, currency, setClientUUID);
  const COLUMNS_CAMPAIGN = useColumnsCampaign(t, setClientUUID);

  return (
    <div className={s['page-container']}>
      {openGenerate && (
        <GeneratePromoModal
          onClose={handleCloseGenerate}
          id={selectedCampaignId}
        />
      )}

      {clientUUID && (
        <ClientModal
          clientUUID={clientUUID}
          onClose={() => setClientUUID(null)}
          setIsDepositOpen={setIsDepositOpen}
        />
      )}

      {isDepositOpen && (
        <DepositModal
          defaultClientUUID={clientUUID}
          onClose={() => {
            setIsDepositOpen(false);
            setClientUUID(null);
          }}
        />
      )}

      {contextMenuDataPromo && cursorPositionPromo && (
        <ContextMenu
          layoutElement={layoutElement}
          data={contextMenuDataPromo}
          items={contextMenuItemsPromo}
          closeContextMenu={closeContextMenuPromo}
          position={cursorPositionPromo}
        />
      )}
      {contextMenuDataCampaign && cursorPositionCampaign && (
        <ContextMenu
          layoutElement={layoutElement}
          data={contextMenuDataCampaign}
          items={contextMenuItemsCampaign}
          closeContextMenu={closeContextMenuCampaign}
          position={cursorPositionCampaign}
        />
      )}
      <div className={s.header}>
        <Tabs
          options={OPTIONS}
          onChange={(value) => {
            setSearchStr('');

            setTab(value);
            setCurrentPagePromo(1);
            setCurrentPageCampaign(1);
            setPromoPaginationInput(currentPagePromo);
            setCampaignPaginationInput(currentPageCampaign);
          }}
          value={tab}
          className={s.tabs}
          itemVariant="row"
        />
        <Input
          variant="secondary"
          value={searchStr}
          onChange={setSearchStr}
          placeholder={t('promo.search')}
          leftContent={<SearchIcon color="primary" />}
          errorWrapperClassName={s.search}
        />

        <Button
          iconName="CirclePlusIcon"
          className={s.add}
          onClick={() => {
            tab === EPromoCodeMode.SINGLE
              ? navigate(`/promo-code`)
              : navigate(`/promo-campaign`);
          }}
        >
          {tab === EPromoCodeMode.SINGLE
            ? t('promos.add')
            : t('promo.add.button')}
        </Button>
      </div>

      <div className={s.list}>
        <Preloader
          loading={promoCodesLoading || promoCampaignLoading}
          hasData={
            tab === EPromoCodeMode.SINGLE
              ? (promoCodes?.promoCodesList?.items?.length || 0) > 0
              : (promoCampaign?.promoCampaignList?.items?.length || 0) > 0
          }
          noDataPlaceholder={
            tab === EPromoCodeMode.SINGLE
              ? t('promos.placeholder')
              : t('promos.campaign.placeholder')
          }
        >
          {tab === EPromoCodeMode.SINGLE ? (
            <>
              <div className={s.data}>
                <Table
                  data={promoCodes?.promoCodesList.items || []}
                  columns={COLUMNS_PROMO}
                  isLoading={promoCodesLoading}
                  getRowClassName={() => s.row}
                  className={s.table}
                  onContextMenu={onContextMenuPromo}
                  onRowDoubleClick={(_, data) => {
                    navigate(`/promo-code/${data?.id}`);
                  }}
                />
              </div>
              {promoCodes?.promoCodesList.paginatorInfo &&
                promoCodes.promoCodesList.paginatorInfo.lastPage > 1 && (
                  <div className={s.pagination_block}>
                    <Pagination
                      onChange={(page) => {
                        if (
                          page ===
                          promoCodes?.promoCodesList?.paginatorInfo?.currentPage
                        )
                          return;
                        setCurrentPagePromo(page);
                        setPromoPaginationInput(page);
                      }}
                      {...promoCodes.promoCodesList.paginatorInfo}
                      className={s['pagination']}
                    />
                    <NumberInput
                      value={promoPaginationInput}
                      placeholder="№"
                      variant="secondary"
                      onEnter={() => {
                        setCurrentPagePromo(promoPaginationInput);
                      }}
                      className={s.input}
                      onChange={(value) => {
                        if (typeof value === 'number') {
                          if (value < 1) {
                            setPromoPaginationInput(1);
                          } else if (
                            value >
                            promoCodes?.promoCodesList?.paginatorInfo?.lastPage
                          ) {
                            setPromoPaginationInput(
                              promoCodes?.promoCodesList?.paginatorInfo
                                ?.lastPage
                            );
                          } else {
                            setPromoPaginationInput(value);
                          }
                        }
                      }}
                    />
                  </div>
                )}
            </>
          ) : (
            <>
              <div className={s.data}>
                <Table
                  data={promoCampaign?.promoCampaignList?.items || []}
                  columns={COLUMNS_CAMPAIGN}
                  isLoading={promoCampaignLoading}
                  getRowClassName={() => s.row}
                  className={s.table}
                  emptyDataPlaceholder={t('achievements.placeholder')}
                  onContextMenu={onContextMenuCampaign}
                  onRowDoubleClick={(_, data) => {
                    navigate(`/promo-campaign/${data?.id}`);
                  }}
                />
              </div>
              {promoCampaign?.promoCampaignList.paginatorInfo &&
                promoCampaign.promoCampaignList.paginatorInfo.lastPage > 1 && (
                  <div className={s.pagination_block}>
                    <Pagination
                      onChange={(page) => {
                        if (
                          page ===
                          promoCampaign?.promoCampaignList?.paginatorInfo
                            ?.currentPage
                        )
                          return;
                        setCurrentPageCampaign(page);
                        setCampaignPaginationInput(page);
                      }}
                      {...promoCampaign.promoCampaignList.paginatorInfo}
                      className={s['pagination']}
                    />
                    <NumberInput
                      value={campaignPaginationInput}
                      placeholder="№"
                      variant="secondary"
                      onEnter={() => {
                        setCurrentPageCampaign(campaignPaginationInput);
                      }}
                      className={s.input}
                      onChange={(value) => {
                        if (typeof value === 'number') {
                          if (value < 1) {
                            setCampaignPaginationInput(1);
                          } else if (
                            value >
                            promoCampaign?.promoCampaignList?.paginatorInfo
                              ?.lastPage
                          ) {
                            setCampaignPaginationInput(
                              promoCampaign?.promoCampaignList?.paginatorInfo
                                ?.lastPage
                            );
                          } else {
                            setCampaignPaginationInput(value);
                          }
                        }
                      }}
                    />
                  </div>
                )}
            </>
          )}
        </Preloader>
      </div>
    </div>
  );
};
